/**
 * Assets Unified Data Hook (Enhanced)
 * 
 * Wraps useAssetsUnified with filtering, sorting, and pagination
 * Drop-in replacement for useAssetsData that queries the unified view
 */

import { useMemo, useState } from 'react';
import { getRowsPerPagePref, setRowsPerPagePref } from '@/utils/tablePreferences';
import { useAssetsUnified, type UnifiedAsset } from './useAssetsUnified';

// Re-export types from useAssetsData for compatibility
export interface AssetsFilters {
  search: string;
  assetTypes: ('cash' | 'equity' | 'fixed_income' | 'alternative' | 'multi_asset')[];
  currencies: string[];
  sourceTypes: ('manual' | 'connected' | 'account')[];
  valueRange: {
    min: number | null;
    max: number | null;
  };
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
}

export interface AssetsSortConfig {
  field: 'asset_name' | 'asset_type' | 'market_value' | 'created_at' | 'symbol' | 'currency' | 'source_type';
  direction: 'asc' | 'desc';
}

export interface AssetsPaginationConfig {
  page: number;
  pageSize: number;
}

export interface AssetsStats {
  totalValue: number;
  totalCount: number;
  byAssetType: Record<string, { count: number; value: number }>;
  byCurrency: Record<string, { count: number; value: number }>;
  bySource?: Record<string, { count: number; value: number }>;
  averageValue: number;
  recentlyAdded: number;
}

export function useAssetsUnifiedData() {
  // Get unified assets (accounts, holdings, manual)
  const { assets: allAssets, isLoading, error, refetch, stats: baseStats } = useAssetsUnified();

  // Local state for filtering, sorting, and pagination
  const [filters, setFilters] = useState<AssetsFilters>({
    search: '',
    assetTypes: [],
    currencies: [],
    sourceTypes: [],
    valueRange: { min: null, max: null },
    dateRange: { start: null, end: null },
  });

  const [sortConfig, setSortConfig] = useState<AssetsSortConfig>({
    field: 'asset_name',
    direction: 'desc',
  });

  const [pagination, setPagination] = useState<AssetsPaginationConfig>({
    page: 1,
    pageSize: getRowsPerPagePref('assets', 25),
  });

  // Filter and process assets
  const processedAssets = useMemo(() => {
    let filtered = [...allAssets];

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(asset => 
        asset.asset_name.toLowerCase().includes(searchLower) ||
        asset.symbol?.toLowerCase().includes(searchLower) ||
        asset.asset_type.toLowerCase().includes(searchLower) ||
        asset.description?.toLowerCase().includes(searchLower)
      );
    }

    // Apply asset type filter
    if (filters.assetTypes.length > 0) {
      filtered = filtered.filter(asset => 
        filters.assetTypes.includes(asset.asset_type as any)
      );
    }

    // Apply currency filter
    if (filters.currencies.length > 0) {
      filtered = filtered.filter(asset => 
        filters.currencies.includes(asset.currency.toUpperCase())
      );
    }

    // Apply source type filter
    if (filters.sourceTypes.length > 0) {
      filtered = filtered.filter(asset => 
        filters.sourceTypes.includes(asset.source_type as any)
      );
    }

    // Apply value range filter
    if (filters.valueRange.min !== null) {
      filtered = filtered.filter(asset => 
        asset.market_value >= filters.valueRange.min!
      );
    }
    if (filters.valueRange.max !== null) {
      filtered = filtered.filter(asset => 
        asset.market_value <= filters.valueRange.max!
      );
    }

    // Apply date range filter (using as_of timestamp)
    if (filters.dateRange.start) {
      filtered = filtered.filter(asset => 
        new Date(asset.as_of) >= filters.dateRange.start!
      );
    }
    if (filters.dateRange.end) {
      filtered = filtered.filter(asset => 
        new Date(asset.as_of) <= filters.dateRange.end!
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortConfig.field) {
        case 'asset_name':
          aValue = a.asset_name.toLowerCase();
          bValue = b.asset_name.toLowerCase();
          break;
        case 'asset_type':
          aValue = a.asset_type;
          bValue = b.asset_type;
          break;
        case 'market_value':
          aValue = a.market_value;
          bValue = b.market_value;
          break;
        case 'created_at':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        case 'symbol':
          aValue = a.symbol?.toLowerCase() || '';
          bValue = b.symbol?.toLowerCase() || '';
          break;
        case 'currency':
          aValue = (a.currency || '').toUpperCase();
          bValue = (b.currency || '').toUpperCase();
          break;
        case 'source_type':
          aValue = a.source_type || '';
          bValue = b.source_type || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [allAssets, filters, sortConfig]);

  // Apply pagination
  const paginatedAssets = useMemo(() => {
    const startIndex = (pagination.page - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return processedAssets.slice(startIndex, endIndex);
  }, [processedAssets, pagination]);

  // Calculate enhanced stats
  const stats = useMemo((): AssetsStats => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const recentlyAdded = processedAssets.filter(asset => 
      new Date(asset.created_at) >= thirtyDaysAgo
    ).length;

    // Calculate by source
    const bySource: Record<string, { count: number; value: number }> = {};
    allAssets.forEach(asset => {
      const source = asset.source_type || 'unknown';
      if (!bySource[source]) {
        bySource[source] = { count: 0, value: 0 };
      }
      bySource[source].count++;
      bySource[source].value += asset.market_value;
    });

    return {
      totalValue: baseStats.totalValue,
      totalCount: baseStats.totalCount,
      byAssetType: baseStats.byAssetType,
      byCurrency: baseStats.byCurrency,
      bySource,
      averageValue: baseStats.totalCount > 0 ? baseStats.totalValue / baseStats.totalCount : 0,
      recentlyAdded,
    };
  }, [baseStats, processedAssets, allAssets]);
  // Filter options
  const filterOptions = useMemo(() => {
    return {
      assetTypes: [...new Set(allAssets.map(asset => asset.asset_type))],
      currencies: [...new Set(allAssets.map(asset => asset.currency.toUpperCase()))].sort(),
      valueRange: {
        min: allAssets.length > 0 ? Math.min(...allAssets.map(asset => asset.market_value)) : 0,
        max: allAssets.length > 0 ? Math.max(...allAssets.map(asset => asset.market_value)) : 0,
      },
    };
  }, [allAssets]);
  // Helper functions
  const updateFilters = (newFilters: Partial<AssetsFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const updateSort = (field: AssetsSortConfig['field']) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      assetTypes: [],
      currencies: [],
      sourceTypes: [],
      valueRange: { min: null, max: null },
      dateRange: { start: null, end: null },
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const goToPage = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const changePageSize = (pageSize: number) => {
    setPagination({ page: 1, pageSize });
    setRowsPerPagePref('assets', pageSize);
  };

  // Pagination info
  const totalPages = Math.ceil(processedAssets.length / pagination.pageSize);
  const hasNextPage = pagination.page < totalPages;
  const hasPrevPage = pagination.page > 1;

  return {
    // Assets data (using UnifiedAsset type which includes source_type and as_of)
    assets: allAssets,
    filteredAssets: processedAssets,
    paginatedAssets,
    isLoading,
    error,
    refetch,
    
    // Stats
    stats,
    filterOptions,
    
    // State
    filters,
    sortConfig,
    pagination,
    
    // Pagination info
    totalPages,
    hasNextPage,
    hasPrevPage,
    totalFilteredCount: processedAssets.length,
    
    // Actions
    updateFilters,
    updateSort,
    resetFilters,
    goToPage,
    changePageSize,
    
    // For backward compatibility with manual assets operations
    // Note: Edit/delete operations should check source_type and handle accordingly
    addAsset: refetch, // Trigger refetch after adding
    updateAsset: refetch, // Trigger refetch after updating
    deleteAsset: refetch, // Trigger refetch after deleting
  };
}
